import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import MainNav from "@/components/MainNav";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { FaNewspaper, FaInstagram, FaExternalLinkAlt, FaSync } from "react-icons/fa";
import { searchArtistNews, getNews } from "@/lib/api";

export default function NewsPage() {
  const [savedNews, setSavedNews] = useState([]);
  const [autoNews, setAutoNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("auto");

  useEffect(() => {
    fetchAllNews();
  }, []);

  const fetchAllNews = async () => {
    try {
      const savedRes = await getNews();
      setSavedNews(savedRes.data || []);
      await fetchAutoNews();
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAutoNews = async () => {
    setSearchLoading(true);
    try {
      const res = await searchArtistNews();
      // Sort articles by date (newest first)
      const articles = res.data?.articles || [];
      const sorted = articles.sort((a, b) => {
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        return dateB - dateA;
      });
      setAutoNews(sorted);
    } catch (error) {
      console.error("Error searching news:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  const displayNews = activeTab === "auto" ? autoNews : savedNews;

  return (
    <div className="min-h-screen bg-background" data-testid="news-page">
      <MainNav />

      <main className="pt-24 pb-16">
        {/* Instagram Feed - Full Width at Top */}
        <div className="max-w-7xl mx-auto px-4 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-3xl md:text-4xl font-syne font-bold text-white" data-testid="news-title">News</h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            data-testid="instagram-section"
          >
            <h2 className="text-lg font-syne font-bold text-white mb-4 flex items-center gap-2" data-testid="instagram-heading">
              <FaInstagram className="text-pink-500" /> Instagram
            </h2>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              {/* Elfsight Instagram Feed - Full Width */}
              <div 
                className="elfsight-app-98ce580a-62df-4349-a74d-2d31bc19b688 w-full" 
                data-elfsight-app-lazy
                style={{ minWidth: '100%' }}
              ></div>
              <a
                href="https://www.instagram.com/u_n_d_e_r_p_l_a_y/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 block text-center text-sm text-zinc-400 hover:text-white transition-colors"
                data-testid="instagram-follow-link"
              >
                Follow @u_n_d_e_r_p_l_a_y
              </a>
            </div>
          </motion.div>
        </div>

        {/* News Articles - Below Instagram */}
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            data-testid="news-articles-section"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-syne font-bold text-white flex items-center gap-2" data-testid="news-articles-heading">
                <FaNewspaper /> Artist News
              </h2>
              <div className="flex items-center gap-2">
                <div className="flex bg-zinc-900 rounded-lg p-1 border border-zinc-800">
                  <button
                    onClick={() => setActiveTab("auto")}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      activeTab === "auto"
                        ? "bg-zinc-700 text-white"
                        : "text-zinc-400 hover:text-white"
                    }`}
                    data-testid="auto-news-tab"
                  >
                    Latest
                  </button>
                  <button
                    onClick={() => setActiveTab("saved")}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      activeTab === "saved"
                        ? "bg-zinc-700 text-white"
                        : "text-zinc-400 hover:text-white"
                    }`}
                    data-testid="saved-news-tab"
                  >
                    Featured
                  </button>
                </div>
                {activeTab === "auto" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchAutoNews}
                    disabled={searchLoading}
                    className="text-zinc-400 hover:text-white"
                    data-testid="refresh-news-btn"
                  >
                    <FaSync className={`w-4 h-4 ${searchLoading ? "animate-spin" : ""}`} />
                  </Button>
                )}
              </div>
            </div>
            
            {loading || (activeTab === "auto" && searchLoading && autoNews.length === 0) ? (
              <div className="grid md:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                    <Skeleton className="h-5 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ))}
              </div>
            ) : displayNews.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-8 text-center" data-testid="no-news-message">
                <FaNewspaper className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
                <p className="text-zinc-500">
                  {activeTab === "auto" 
                    ? "No news found for managed artists." 
                    : "No featured news articles yet."}
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4" data-testid="news-list">
                {displayNews.map((article, index) => (
                  <motion.a
                    key={article.id || article.url || index}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.03 }}
                    className="block bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 hover:border-zinc-600 hover:bg-zinc-800/30 transition-all group"
                    data-testid={`news-article-${index}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-white group-hover:text-zinc-200 mb-2 line-clamp-2 text-sm">
                          {article.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                          {article.date && (
                            <span className="font-medium">{formatDate(article.date)}</span>
                          )}
                          {article.source && (
                            <span className="truncate max-w-[120px]">• {article.source}</span>
                          )}
                          {article.artist && (
                            <span className="px-2 py-0.5 bg-zinc-800 text-zinc-300 rounded-full">
                              {article.artist}
                            </span>
                          )}
                        </div>
                      </div>
                      <FaExternalLinkAlt className="text-zinc-600 group-hover:text-zinc-400 flex-shrink-0 mt-1 w-3 h-3" />
                    </div>
                  </motion.a>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </main>

      <footer className="border-t border-zinc-800 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-zinc-600 text-xs">© 2025 Underplay. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
